import Controls from "./Controls";
import styles from "./App.module.scss";
import "./theme/styles.scss";
import { Leadspace } from "./Leadspace";
import { ArtContext, useArtSpecs } from "./Context";
import { Dots } from "./Dots";
import { Art } from "./Art";

export default function App() {
  return (
    <ArtContext>
      <div className={styles.page}>
        <header className={styles.nav}>
          <div className={styles.navMark}>IBM</div>
          <div className={styles.navDivider} />
          <div className={styles.navTitle}>Leadspace Art Generation</div>
          <div className={styles.navMeta}>Prototype</div>
        </header>

        <main className={styles.main}>
          <div className={styles.header}>
            <div className={styles.eyebrow}>Generative art</div>
            <h1 className={styles.title}>Leadspace art generation</h1>
            <p className={styles.lede}>
              Enter a product EDG ID and set its lever, shape and category to generate a
              deterministic leadspace composition.
            </p>
          </div>

          <div className={styles.body}>
            <Controls />
            <div className={styles.stage}>
              <Leadspace>
                <Dots />
                <Art />
              </Leadspace>
              <Save />
              <Config />
            </div>
          </div>
        </main>

        <footer className={styles.footer}>
          <div className={styles.footerMark}>IBM</div>
          <div className={styles.footerNote}>
            Leadspace art generation prototype. Compositions are seeded from the EDG ID, so the same
            ID always produces the same artwork.
          </div>
        </footer>
      </div>
    </ArtContext>
  );
}

const Config = () => {
  const { specs, inputs } = useArtSpecs();
  return (
    <details className={styles.config}>
      <summary className={styles.configSummary}>Configuration</summary>
      <div className={styles.configGrid}>
        <div className={styles.configBlock}>
          <div className={styles.configLabel}>Inputs</div>
          <pre className={styles.code}>{JSON.stringify(inputs, null, 2)}</pre>
        </div>
        <div className={styles.configBlock}>
          <div className={styles.configLabel}>Specs</div>
          <pre className={styles.code}>{JSON.stringify(specs, null, 2)}</pre>
        </div>
      </div>
    </details>
  );
};

const Save = () => {
  const { bp, ref, width, height } = useArtSpecs();

  const saveAsPNG = (transparent) => {
    var svgElement = ref.current;
    var svgData = new XMLSerializer().serializeToString(svgElement);
    var svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    var url = URL.createObjectURL(svgBlob);

    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    var img = new Image();

    // Adjust for device pixel ratio
    var scale = window.devicePixelRatio || 1;

    canvas.width = width * scale;
    canvas.height = height * scale;

    canvas.style.width = width + "px";
    canvas.style.height = height + "px";

    img.onload = function () {
      ctx.setTransform(scale, 0, 0, scale, 0, 0); // Properly set the scale transform
      if (!transparent) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height); // Draw background without scaling
      }

      // Draw the dots
      const dx = bp < 672 ? width / 40 : bp < 1056 ? width / 56 : 16;
      const dy = bp < 672 ? height / 30 : bp < 1056 ? height / 28 : 16;
      const r = 1;

      for (let x = dx; x < width - 1; x += dx) {
        for (let y = dy; y < height - 1; y += dy) {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, 2 * Math.PI);
          ctx.fillStyle = "#E0E0E0";
          ctx.fill();
        }
      }

      // Draw SVG image
      ctx.drawImage(img, 0, 0, bp < 1056 ? width : bp, height);
      URL.revokeObjectURL(url);

      var pngData = canvas.toDataURL("image/png", 1);

      var downloadLink = document.createElement("a");
      downloadLink.href = pngData;
      downloadLink.download = "image.png";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    img.src = url;
  };

  return (
    <div className={styles.actions}>
      <button className="button--tertiary" onClick={() => navigator.clipboard.writeText(ref.current.outerHTML)}>
        Copy SVG
      </button>
      <button onClick={() => saveAsPNG()}>Save PNG, white background</button>
      <button className="button--secondary" onClick={() => saveAsPNG(true)}>
        Save PNG, transparent
      </button>
    </div>
  );
};
